import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatDate, formatPhone } from '../utils/dateUtils';
import { User, Phone, Mail, Calendar, Heart, Activity, ClipboardList, Dumbbell, Clock, DollarSign, Users } from 'lucide-react';

const sexLabels = { M: 'Masculino', F: 'Feminino', Outro: 'Outro' };
const diaLabels = { seg: 'Seg', ter: 'Ter', qua: 'Qua', qui: 'Qui', sex: 'Sex', sab: 'Sáb', dom: 'Dom' };
const formatBRL = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const PilatesCard = ({ pilates }) => {
  if (!pilates) return null;
  const { turma, financeiro, clinica } = pilates;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Dumbbell className="w-5 h-5 text-purple-600" />
        <h2 className="text-lg font-semibold text-gray-900">Pilates</h2>
        {clinica && <span className="ml-auto text-xs text-gray-400">{clinica}</span>}
      </div>

      {turma ? (
        <div className="space-y-3">
          <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-purple-600" />
              <p className="font-bold text-purple-900">{turma.nome}</p>
            </div>
            {turma.professor && <p className="text-xs text-purple-700 mb-2">Professor(a): {turma.professor}</p>}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(turma.diasSemana || []).map(d => (
                <span key={d} className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                  {diaLabels[d] || d}
                </span>
              ))}
            </div>
            {(turma.horarioInicio || turma.horarioFim) && (
              <div className="flex items-center gap-1.5 text-xs text-purple-700">
                <Clock className="w-3.5 h-3.5" />
                {turma.horarioInicio} {turma.horarioFim && `– ${turma.horarioFim}`}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-3">Você ainda não foi adicionado(a) a uma turma.</p>
      )}

      {financeiro && (
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <p className="text-sm font-semibold text-gray-700">Mensalidade</p>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
            <div>
              <p className="text-sm font-bold text-gray-900">{formatBRL(financeiro.mensalidade)}</p>
              {financeiro.diaVencimento && (
                <p className="text-xs text-gray-500">Vencimento dia {financeiro.diaVencimento}</p>
              )}
            </div>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
              financeiro.pago ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
            }`}>
              {financeiro.pago ? 'Pago' : 'Pendente'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const MeuPainelPage = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const { data } = await api.get('/meu-dashboard');
        setDashboardData(data.data);
      } catch (error) {
        toast.error('Erro ao carregar dados do painel');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-red-600">Erro ao carregar dados</p>
      </div>
    );
  }

  const { patient, sessions, pilates, temFisioterapia } = dashboardData;
  const isPilatesOnly = !temFisioterapia;

  return (
    <div className="min-h-screen bg-gray-50 p-6">

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Meu Painel</h1>
        <p className="text-gray-500 mt-1">
          {isPilatesOnly ? 'Acompanhe sua turma e mensalidade de pilates' : 'Acompanhe seu tratamento e histórico de sessões'}
        </p>
      </div>

      <div className={`grid grid-cols-1 ${isPilatesOnly ? '' : 'lg:grid-cols-3'} gap-6`}>

        {/* Coluna principal */}
        <div className={`${isPilatesOnly ? '' : 'lg:col-span-2'} space-y-6`}>

          {/* Meus Dados */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Meus Dados</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <User className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Nome</p>
                  <p className="text-sm font-semibold text-gray-900">{patient.nome}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Telefone</p>
                  <p className="text-sm font-semibold text-gray-900">{formatPhone(patient.telefone)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Data de Nascimento</p>
                  <p className="text-sm font-semibold text-gray-900">{formatDate(patient.dateOfBirth)}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center">
                  <Heart className="w-4 h-4 text-pink-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sexo</p>
                  <p className="text-sm font-semibold text-gray-900">{sexLabels[patient.sex] || patient.sex}</p>
                </div>
              </div>

              {patient.email && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl md:col-span-2">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm font-semibold text-gray-900">{patient.email}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Diagnóstico */}
            {patient.diagnosisTitle && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Diagnóstico</h3>
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="font-bold text-amber-900">{patient.diagnosisTitle}</p>
                  {patient.diagnosisDescription && (
                    <p className="text-sm text-amber-800 mt-1">{patient.diagnosisDescription}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Pilates */}
          {pilates && <PilatesCard pilates={pilates} />}

          {/* Minhas Sessões — só faz sentido para pacientes de fisioterapia */}
          {!isPilatesOnly && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Minhas Sessões</h2>
                <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                  {sessions.length} sessões
                </span>
              </div>

              {sessions.length === 0 ? (
                <p className="text-gray-500 text-sm">Nenhuma sessão agendada ainda.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">Sessão</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3 pr-4">Data</th>
                        <th className="text-left text-xs font-semibold text-gray-500 uppercase pb-3">Horário</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {sessions.map((session) => (
                        <tr key={session._id} className="hover:bg-gray-50">
                          <td className="py-3 pr-4 text-sm font-medium text-gray-900">
                            Sessão {session.sessionNumber}
                          </td>
                          <td className="py-3 pr-4 text-sm text-gray-500">
                            {formatDate(session.sessionDate)}
                          </td>
                          <td className="py-3 text-sm text-gray-500">
                            {session.sessionTime || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Coluna lateral — Evoluções (só para pacientes de fisioterapia) */}
        {!isPilatesOnly && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-4">
              <ClipboardList className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Minhas Evoluções</h2>
            </div>

            {sessions.filter(s => s.evolution).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                  <ClipboardList className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">Nenhuma evolução registrada ainda.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                {sessions
                  .filter(s => s.evolution)
                  .map((session) => (
                    <div key={session._id} className="p-4 border border-gray-100 rounded-xl hover:border-gray-200 transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                          Sessão {session.sessionNumber}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatDate(session.evolution.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{session.evolution.description}</p>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default MeuPainelPage;